import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  X, ShieldCheck, Lock, Fingerprint, Eye, EyeOff, 
  Smartphone, Bell, UserX, Trash2, ShieldAlert,
  ChevronRight, CheckCircle2, AlertTriangle, Info,
  Cpu, Activity, Terminal, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { AIFirewallDashboard } from './AIFirewallDashboard';

interface SecurityCenterProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  preferences: any;
  onUpdatePreferences: (prefs: any) => void;
}

export function SecurityCenter({ isOpen, onClose, user, preferences, onUpdatePreferences }: SecurityCenterProps) {
  const [activeTab, setActiveTab] = React.useState<'overview' | 'privacy' | 'firewall' | 'audit'>('overview');
  const [isChangingPin, setIsChangingPin] = React.useState(false);
  const [newPin, setNewPin] = React.useState('');
  const [confirmPin, setConfirmPin] = React.useState('');
  const [step, setStep] = React.useState<'new' | 'confirm'>('new');

  const securityScore = 85; // Mock score

  const handlePinChange = () => {
    if (newPin.length !== 4) {
      toast.error("Invalid PIN", { description: "PIN must be exactly 4 digits." });
      return;
    }
    
    if (step === 'new') {
      setStep('confirm');
      return;
    }

    if (newPin !== confirmPin) {
      toast.error("Mismatch", { description: "PINs do not match. Try again." });
      setConfirmPin('');
      setStep('new');
      setNewPin('');
      return;
    }

    onUpdatePreferences({
      ...preferences,
      safeCode: newPin
    });
    
    toast.success("Safe Code Updated!", {
      description: "Your new emergency PIN has been saved securely."
    });
    
    setIsChangingPin(false);
    setNewPin('');
    setConfirmPin('');
    setStep('new');
  };

  const togglePreference = (key: string) => {
    onUpdatePreferences({
      ...preferences,
      [key]: !preferences[key]
    });
    toast.success(`${key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} updated!`);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              onClose();
              setIsChangingPin(false);
              setStep('new');
              setNewPin('');
              setConfirmPin('');
            }}
            className="absolute inset-0 bg-zinc-900/80 backdrop-blur-md"
          />
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative w-full max-w-md bg-white rounded-[40px] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
          >
            {/* Header */}
            <div className="bg-zinc-900 p-8 text-white relative shrink-0">
              <button 
                onClick={() => {
                  onClose();
                  setIsChangingPin(false);
                  setStep('new');
                  setNewPin('');
                  setConfirmPin('');
                }}
                className="absolute top-6 right-6 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-green-600 flex items-center justify-center shadow-lg shadow-green-600/30">
                  <ShieldCheck className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-black italic uppercase tracking-tighter leading-none mb-1">Security Center</h2>
                  <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Sentlo Trust & Safety</p>
                </div>
              </div>

              {/* Security Score */}
              <div className="bg-white/10 rounded-2xl p-4 border border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-black uppercase tracking-widest">Protection Level</span>
                  <span className="text-[10px] font-black uppercase tracking-widest text-green-400">Excellent</span>
                </div>
                <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${securityScore}%` }}
                    className="h-full bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]"
                  />
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex border-b border-zinc-100 px-4 py-2 bg-zinc-50/50">
              {['overview', 'privacy', 'firewall', 'audit'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab as any);
                    setIsChangingPin(false);
                    setStep('new');
                    setNewPin('');
                    setConfirmPin('');
                  }}
                  className={`flex-1 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${
                    activeTab === tab && !isChangingPin ? 'text-green-600' : 'text-zinc-400'
                  }`}
                >
                  {tab === 'audit' ? 'Lifecycle' : tab}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 no-scrollbar">
              {activeTab === 'overview' && !isChangingPin && (
                <div className="space-y-6">
                  <section className="space-y-3">
                    <h3 className="text-xs font-black uppercase tracking-widest text-zinc-400 px-2">Essential Protection</h3>
                    
                    <div className="bg-zinc-50 rounded-3xl p-5 border border-zinc-100 flex items-center justify-between group hover:border-green-600/20 transition-all">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-zinc-900 border border-zinc-100 shadow-sm">
                          <Fingerprint className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-zinc-900 leading-none mb-1">Biometric Lock</p>
                          <p className="text-[10px] text-zinc-500 font-medium leading-none">Use FaceID/TouchID for app access</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => togglePreference('biometricsEnabled')}
                        className={`w-12 h-6 rounded-full transition-all relative ${preferences.biometricsEnabled ? 'bg-green-600' : 'bg-zinc-200'}`}
                      >
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${preferences.biometricsEnabled ? 'left-7' : 'left-1'}`} />
                      </button>
                    </div>

                    <div className="bg-zinc-50 rounded-3xl p-5 border border-zinc-100 flex items-center justify-between group hover:border-green-600/20 transition-all">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-zinc-900 border border-zinc-100 shadow-sm">
                          <Lock className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-zinc-900 leading-none mb-1">Wallet PIN Lock</p>
                          <p className="text-[10px] text-zinc-500 font-medium leading-none">Secure your coins and SafePay</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => togglePreference('walletLock')}
                        className={`w-12 h-6 rounded-full transition-all relative ${preferences.walletLock ? 'bg-green-600' : 'bg-zinc-200'}`}
                      >
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${preferences.walletLock ? 'left-7' : 'left-1'}`} />
                      </button>
                    </div>
                  </section>

                  <section className="bg-amber-50 rounded-3xl p-5 border border-amber-100">
                    <div className="flex items-center gap-3 mb-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      <p className="text-xs font-black uppercase text-amber-900 tracking-tight">Security Alert</p>
                    </div>
                    <p className="text-[11px] text-amber-700 leading-relaxed font-medium">
                      Your Safe Code is set to default. For better security during SOS events, please change it to a unique 4-digit number.
                    </p>
                    <button 
                      onClick={() => setIsChangingPin(true)}
                      className="mt-4 w-full bg-amber-600 text-white py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-amber-600/20 active:scale-[0.98] transition-all"
                    >
                      Change Safe Code
                    </button>
                  </section>
                </div>
              )}

              {isChangingPin && (
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <button 
                      onClick={() => {
                        setIsChangingPin(false);
                        setStep('new');
                        setNewPin('');
                        setConfirmPin('');
                      }}
                      className="p-2 bg-zinc-100 rounded-full hover:bg-zinc-200 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4 rotate-180" />
                    </button>
                    <h3 className="text-sm font-black uppercase tracking-widest text-zinc-900">
                      {step === 'new' ? 'New Safe Code' : 'Confirm New Code'}
                    </h3>
                  </div>

                  <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-tight px-2 leading-relaxed">
                    {step === 'new' 
                      ? 'Enter a new 4-digit code for emergency SOS use only.' 
                      : 'Please re-enter your new 4-digit code to confirm.'}
                  </p>

                  <div className="flex justify-center gap-3">
                    {[0, 1, 2, 3].map((i) => (
                      <div 
                        key={i}
                        className={`w-12 h-16 rounded-2xl border-2 flex items-center justify-center text-xl font-black ${
                          (step === 'new' ? newPin[i] : confirmPin[i]) 
                            ? 'border-green-600 bg-green-50 text-green-700 shadow-inner' 
                            : 'border-zinc-200 bg-zinc-50 text-zinc-400'
                        }`}
                      >
                        {(step === 'new' ? newPin[i] : confirmPin[i]) ? '●' : ''}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-3 gap-3 pt-4">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'C', 0, 'OK'].map((key) => (
                      <button
                        key={key}
                        onClick={() => {
                          if (key === 'C') {
                            if (step === 'new') setNewPin(prev => prev.slice(0, -1));
                            else setConfirmPin(prev => prev.slice(0, -1));
                          } else if (key === 'OK') {
                            const pinToVerify = step === 'new' ? newPin : confirmPin;
                            if (pinToVerify.length === 4) handlePinChange();
                            else toast.error("PIN incomplete", { description: "Please enter 4 digits." });
                          } else {
                            const pinToUpdate = step === 'new' ? newPin : confirmPin;
                            if (pinToUpdate.length < 4) {
                              if (step === 'new') setNewPin(prev => prev + key);
                              else setConfirmPin(prev => prev + key);
                            }
                          }
                        }}
                        className={`h-16 rounded-2xl font-black text-lg active:scale-95 transition-all shadow-sm flex items-center justify-center ${
                          key === 'OK' 
                            ? 'bg-green-600 text-white shadow-green-600/20' 
                            : key === 'C'
                            ? 'bg-amber-100 text-amber-600'
                            : 'bg-white border border-zinc-100 text-zinc-900'
                        }`}
                      >
                        {key === 'OK' ? <CheckCircle2 className="w-6 h-6" /> : key}
                      </button>
                    ))}
                  </div>

                  <div className="bg-zinc-50 p-4 rounded-3xl border border-zinc-100 flex items-start gap-3">
                    <Info className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5" />
                    <p className="text-[9px] text-zinc-500 font-medium leading-relaxed">
                      Your Safe Code is used in critical SOS situations to verify your safety when law enforcement or emergency responders arrive.
                    </p>
                  </div>
                </div>
              )}

              {activeTab === 'privacy' && !isChangingPin && (
                <div className="space-y-4">
                  <h3 className="text-xs font-black uppercase tracking-widest text-zinc-400 px-2">Robust Data Protection</h3>
                  
                  <div className="bg-zinc-900 rounded-[32px] p-6 text-white relative overflow-hidden mb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-green-400" />
                        <span className="text-[10px] font-black uppercase tracking-widest">Vault Status</span>
                      </div>
                      <div className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full text-[8px] font-black uppercase">Active</div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center text-[10px] font-bold">
                        <span className="text-white/60 uppercase">AES-256 Encryption</span>
                        <CheckCircle2 className="w-3 h-3 text-green-400" />
                      </div>
                      <div className="flex justify-between items-center text-[10px] font-bold">
                        <span className="text-white/60 uppercase">Field-level Masking</span>
                        <CheckCircle2 className="w-3 h-3 text-green-400" />
                      </div>
                      <div className="flex justify-between items-center text-[10px] font-bold">
                        <span className="text-white/60 uppercase">Client-side Hashing</span>
                        <CheckCircle2 className="w-3 h-3 text-green-400" />
                      </div>
                    </div>
                  </div>

                  {[
                    { id: 'ghostMode', icon: EyeOff, title: 'Ghost Mode', desc: 'Hide your location from the public map', color: 'text-purple-600' },
                    { id: 'privateProfile', icon: UserX, title: 'Private Profile', desc: 'Only verified neighbors can see your posts', color: 'text-blue-600' },
                    { id: 'anonymousReporting', icon: Smartphone, title: 'Anonymous Reports', desc: 'Always report without showing your name', color: 'text-zinc-600' },
                  ].map((item) => (
                    <div key={item.id} className="bg-zinc-50 rounded-3xl p-5 border border-zinc-100 flex items-center justify-between group hover:border-green-600/20 transition-all">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl bg-white flex items-center justify-center border border-zinc-100 shadow-sm ${item.color}`}>
                          <item.icon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-zinc-900 leading-none mb-1">{item.title}</p>
                          <p className="text-[10px] text-zinc-500 font-medium leading-none">{item.desc}</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => togglePreference(item.id)}
                        className={`w-12 h-6 rounded-full transition-all relative ${preferences[item.id] ? 'bg-green-600' : 'bg-zinc-200'}`}
                      >
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${preferences[item.id] ? 'left-7' : 'left-1'}`} />
                      </button>
                    </div>
                  ))}

                  <div className="mt-6 p-4 bg-zinc-900 rounded-3xl text-white">
                    <div className="flex items-center gap-3 mb-2">
                      <ShieldAlert className="w-5 h-5 text-red-500" />
                      <p className="text-xs font-black uppercase tracking-tight italic">Sentlo Promise</p>
                    </div>
                    <p className="text-[10px] text-zinc-400 leading-relaxed font-medium">
                      We use bank-grade AES-256 encryption for all neighborhood chats and biometric data. Sentlo never sells your phone number to 3rd parties.
                    </p>
                  </div>
                </div>
              )}

              {activeTab === 'firewall' && !isChangingPin && (
                <AIFirewallDashboard threatLevel="low" />
              )}

              {activeTab === 'audit' && !isChangingPin && (
                <div className="space-y-6">
                  <h3 className="text-xs font-black uppercase tracking-widest text-zinc-400 px-2">Secure Lifecycle Audit</h3>
                  
                  <div className="space-y-4">
                    {[
                      { 
                        title: 'Design-Phase Security', 
                        desc: 'Strict threat modeling and PII impact assessments performed for all new features.', 
                        status: 'Verified',
                        icon: ShieldCheck 
                      },
                      { 
                        title: 'Secure Development', 
                        desc: 'Automated SAST/DAST scanning integrated into our CI/CD pipelines.', 
                        status: 'Protected',
                        icon: Smartphone 
                      },
                      { 
                        title: 'Production Integrity', 
                        desc: '24/7 endpoint monitoring and real-time prompt injection filtering.', 
                        status: 'Live',
                        icon: Bell 
                      }
                    ].map((item, idx) => (
                      <div key={idx} className="bg-zinc-50 rounded-3xl p-5 border border-zinc-100 relative overflow-hidden">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-zinc-400 border border-zinc-100 shrink-0">
                            <item.icon className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-sm font-black italic uppercase tracking-tight text-zinc-900">{item.title}</p>
                              <span className="text-[8px] font-black bg-zinc-900 text-white px-1.5 py-0.5 rounded-full uppercase">{item.status}</span>
                            </div>
                            <p className="text-[10px] text-zinc-500 font-medium leading-relaxed">{item.desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-blue-50 p-5 rounded-3xl border border-blue-100">
                    <div className="flex items-center gap-2 mb-2 text-blue-900">
                      <Info className="w-4 h-4" />
                      <span className="text-[10px] font-black uppercase tracking-widest">Compliance</span>
                    </div>
                    <p className="text-[10px] text-blue-700 leading-relaxed font-medium">
                      Our secure development lifecycle (SDLC) is aligned with global standards to ensure that Nigerian community data remains sovereign and private.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Action */}
            <div className="p-6 bg-zinc-50 shrink-0 border-t border-zinc-100">
              <button
                onClick={onClose}
                className="w-full bg-zinc-900 text-white py-4 rounded-2xl font-black uppercase tracking-widest hover:bg-black transition-all shadow-xl shadow-zinc-900/10 active:scale-[0.98]"
              >
                Safe & Secure
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
